import { useQuery } from '@tanstack/react-query'
import { Address } from 'viem'
import { OracleAdapter, OracleChecksStatus, UseOracleAdaptersParams } from './types'
import { fetchOracleAdapters } from './fetchOracleAdapters'

/**
 * Hook to fetch all oracle adapters from Euler oracle checks API
 * Uses server action to avoid CORS issues with the oracle dashboard
 */
export function useOracleAdapters({ chainId, enabled = true }: UseOracleAdaptersParams) {
  return useQuery<OracleAdapter[]>({
    queryKey: ['oracleAdapters', chainId],
    queryFn: () => fetchOracleAdapters(chainId),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled,
  })
}

/**
 * Get oracle adapters for specific addresses
 */
export function selectOracleAdaptersForAddresses(
  adapters: OracleAdapter[] | undefined,
  addresses: Address[],
): OracleAdapter[] {
  if (!adapters || !addresses.length) return []

  const addressSet = new Set(addresses.map((a) => a.toLowerCase()))

  return adapters.filter(
    (adapter) =>
      addressSet.has(adapter.address.toLowerCase()) ||
      addressSet.has(adapter.base.toLowerCase()) ||
      addressSet.has(adapter.quote.toLowerCase()),
  )
}

/**
 * Get checks status (positive, negative, or mixed)
 */
export function selectChecksStatus(adapter: OracleAdapter): OracleChecksStatus {
  if (!adapter.checks?.length) return 'positive'

  const failedChecks = adapter.checks.filter((check) => !check.pass)
  if (failedChecks.length === 0) return 'positive'
  if (failedChecks.length === adapter.checks.length) return 'negative'
  return 'mixed'
}
